"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.sync = exports.scanAccounts = void 0;
const wallet_btc_1 = require("./wallet-btc");
const bignumber_js_1 = require("bignumber.js");
const logs_1 = require("@ledgerhq/logs");
const cryptoassets_1 = require("@ledgerhq/cryptoassets");
const jsHelpers_1 = require("../../bridge/jsHelpers");
const account_1 = require("../../account");
const derivation_1 = require("@ledgerhq/coin-framework/derivation");
const logic_1 = require("./logic");
const wallet_btc_2 = __importDefault(require("./wallet-btc"));
const logic_2 = require("./logic");
const index_1 = require("../../account/index");
const performance_1 = require("../../performance");
// Map LL's DerivationMode to wallet-btc's
const toWalletDerivationMode = (mode) => {
    if ((0, derivation_1.isTaprootDerivationMode)(mode)) {
        return wallet_btc_1.DerivationModes.TAPROOT;
    }
    if ((0, derivation_1.isNativeSegwitDerivationMode)(mode)) {
        return wallet_btc_1.DerivationModes.NATIVE_SEGWIT;
    }
    if ((0, derivation_1.isSegwitDerivationMode)(mode)) {
        return wallet_btc_1.DerivationModes.SEGWIT;
    }
    return wallet_btc_1.DerivationModes.LEGACY;
};
// Map LL's currency ID to wallet-btc's Account.params.network
const toWalletNetwork = (currencyId) => {
    return (0, cryptoassets_1.getCryptoCurrencyById)(currencyId).isTestnetFor ? "testnet" : "mainnet";
};
// Map wallet-btc's Output to LL's BitcoinOutput
const fromWalletUtxo = (utxo, changeAddresses) => {
    return {
        hash: utxo.output_hash,
        outputIndex: utxo.output_index,
        blockHeight: utxo.block_height,
        address: utxo.address,
        value: new bignumber_js_1.BigNumber(utxo.value),
        rbf: utxo.rbf,
        isChange: changeAddresses.has(utxo.address),
    };
};
// wallet-btc limitation: returns all transactions twice (for each side of the tx)
// so we need to deduplicate them...
const deduplicateOperations = (operations) => {
    const seen = {};
    const out = [];
    let j = 0;
    for (const operation of operations) {
        if (operation) {
            if (seen[operation.id] !== 1) {
                seen[operation.id] = 1;
                out[j++] = operation;
            }
        }
    }
    return out;
};
const makeGetAccountShape = (signerContext) => (info) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    let span;
    const { currency, index, derivationPath, derivationMode, initialAccount, deviceId } = info;
    // In case we get a full derivation path, extract the seed identification part
    // 44'/0'/0'/0/0 --> 44'/0'
    // FIXME Only the CLI provides a full derivationPath: why?
    const rootPath = derivationPath.split("/", 2).join("/");
    const accountPath = `${rootPath}/${index}'`;
    const paramXpub = initialAccount ? (0, index_1.decodeAccountId)(initialAccount.id).xpubOrAddress : undefined;
    let generatedXpub;
    if (!paramXpub) {
        // Xpub not provided, generate it using the hwapp
        if (deviceId === undefined || deviceId === null) {
            throw new Error("deviceId required to generate the xpub");
        }
        const { bitcoinLikeInfo } = currency;
        const { XPUBVersion: xpubVersion } = bitcoinLikeInfo;
        generatedXpub = yield signerContext(deviceId, currency, signer => signer.getWalletXpub({
            path: accountPath,
            xpubVersion,
        }));
    }
    const xpub = paramXpub || generatedXpub;
    const accountId = (0, account_1.encodeAccountId)({
        type: "js",
        version: "2",
        currencyId: currency.id,
        xpubOrAddress: xpub,
        derivationMode,
    });
    const walletNetwork = toWalletNetwork(currency.id);
    const walletDerivationMode = toWalletDerivationMode(derivationMode);
    span = (0, performance_1.startSpan)("sync", "generateAccount");
    const walletAccount = ((_a = initialAccount === null || initialAccount === void 0 ? void 0 : initialAccount.bitcoinResources) === null || _a === void 0 ? void 0 : _a.walletAccount) ||
        (yield wallet_btc_2.default.generateAccount({
            xpub,
            path: rootPath,
            index,
            currency: currency.id,
            network: walletNetwork,
            derivationMode: walletDerivationMode,
        }, currency));
    span.finish();
    const oldOperations = (initialAccount === null || initialAccount === void 0 ? void 0 : initialAccount.operations) || [];
    const [, currentBlock] = yield Promise.all([
        wallet_btc_2.default.syncAccount(walletAccount),
        walletAccount.xpub.explorer.getCurrentBlock(),
    ]);
    const blockHeight = currentBlock === null || currentBlock === void 0 ? void 0 : currentBlock.height;
    const balance = yield wallet_btc_2.default.getAccountBalance(walletAccount);
    span = (0, performance_1.startSpan)("sync", "getAccountTransactions");
    const { txs: transactions } = yield wallet_btc_2.default.getAccountTransactions(walletAccount);
    span.finish();
    span = (0, performance_1.startSpan)("sync", "getXpubAddresses");
    const accountAddresses = new Set();
    const accountAddressesWithInfo = yield walletAccount.xpub.getXpubAddresses();
    accountAddressesWithInfo.forEach(a => accountAddresses.add(a.address));
    span.finish();
    span = (0, performance_1.startSpan)("sync", "getUniquesAddresses");
    const changeAddresses = new Set();
    const changeAddressesWithInfo = yield walletAccount.xpub.storage.getUniquesAddresses({
        account: 1,
    });
    changeAddressesWithInfo.forEach(a => changeAddresses.add(a.address));
    span.finish();
    span = (0, performance_1.startSpan)("sync", "mapTxToOperations");
    const newOperations = transactions === null || transactions === void 0 ? void 0 : transactions.map(tx => (0, logic_2.mapTxToOperations)(tx, currency.id, accountId, accountAddresses, changeAddresses)).flat();
    span.finish();
    span = (0, performance_1.startSpan)("sync", "unify operations");
    const newUniqueOperations = deduplicateOperations(newOperations);
    const operations = (0, jsHelpers_1.mergeOps)(oldOperations, newUniqueOperations);
    span.finish();
    span = (0, performance_1.startSpan)("sync", "gather utxos");
    const rawUtxos = yield wallet_btc_2.default.getAccountUnspentUtxos(walletAccount);
    const utxos = rawUtxos.map(utxo => fromWalletUtxo(utxo, changeAddresses));
    span.finish();
    return {
        id: accountId,
        xpub,
        balance,
        spendableBalance: balance,
        operations,
        operationsCount: operations.length,
        freshAddress: walletAccount.xpub.freshAddress,
        freshAddressPath: `${accountPath}/0/${walletAccount.xpub.freshAddressIndex}`,
        blockHeight,
        bitcoinResources: {
            utxos,
            walletAccount,
        },
    };
});
const postSync = (initial, synced) => {
    var _a;
    (0, logs_1.log)("bitcoin/postSync", "bitcoinResources");
    const perCoin = logic_1.perCoinLogic[synced.currency.id];
    const syncedBtc = synced;
    if (perCoin) {
        const { postBuildBitcoinResources, syncReplaceAddress } = perCoin;
        if (postBuildBitcoinResources) {
            syncedBtc.bitcoinResources = postBuildBitcoinResources(syncedBtc, syncedBtc.bitcoinResources);
        }
        if (syncReplaceAddress) {
            syncedBtc.freshAddress = syncReplaceAddress(syncedBtc.freshAddress);
            syncedBtc.freshAddresses = syncedBtc.freshAddresses.map(a => (Object.assign(Object.assign({}, a), { address: syncReplaceAddress(a.address) })));
            if (syncedBtc.bitcoinResources) {
                syncedBtc.bitcoinResources.utxos = (_a = syncedBtc.bitcoinResources) === null || _a === void 0 ? void 0 : _a.utxos.map(u => (Object.assign(Object.assign({}, u), { address: u.address && syncReplaceAddress(u.address) })));
            }
        }
    }
    (0, logs_1.log)("bitcoin/postSync", "bitcoinResources DONE");
    return syncedBtc;
};
const scanAccounts = (signerContext) => (0, jsHelpers_1.makeScanAccounts)({
    getAccountShape: makeGetAccountShape(signerContext),
});
exports.scanAccounts = scanAccounts;
const sync = (signerContext) => (0, jsHelpers_1.makeSync)({ getAccountShape: makeGetAccountShape(signerContext), postSync });
exports.sync = sync;
//# sourceMappingURL=js-synchronisation.js.map